import { AILoading } from "@components:AILoading";
import { useCopy } from "@hooks:useCopy";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { STREAMING_CURSOR } from "@pages:app/talk/const";
import { MarkdownWithReferences } from "@pages:app/talk/shared/MarkdownWithReferences";
import { MessageUsage } from "@pages:app/talk/shared/MessageUsage";
import { TalkFeedback } from "@pages:app/talk/shared/TalkFeedback";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { IoCheckmarkDoneOutline } from "react-icons/io5";
import { LuCopy } from "react-icons/lu";
import { MdExpandLess, MdExpandMore } from "react-icons/md";

interface AIResponseProps {
  answer?: string;
  references: any[];
  setShowReferences: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  showReferences: boolean;
  llmQuestion?: string;
  messageId?: string | null | undefined;
}

export const AIResponse = ({
  answer,
  references,
  showReferences,
  setShowReferences,
  llmQuestion,
  messageId,
}: AIResponseProps) => {
  const { t } = useTranslation();
  const { copy, isCopied } = useCopy();
  const isStreaming = (answer ?? "").trimEnd().endsWith(STREAMING_CURSOR);

  return (
    <>
      {isEmpty(answer) ? (
        <AILoading />
      ) : (
        <Box>
          {llmQuestion && (
            <Typography
              variant="subtitle1"
              style={{ fontStyle: "italic", marginBottom: "1rem" }}
            >
              {t("chat.aiQuestion", { question: llmQuestion })}
            </Typography>
          )}

          <MarkdownWithReferences text={answer || ""} references={references} />
          <Box
            sx={{
              marginTop: "1rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {Array.isArray(references) && references.length > 0 ? (
              <Button
                variant="text"
                onClick={() => setShowReferences((prevState) => !prevState)}
                startIcon={
                  showReferences ? (
                    <MdExpandLess color="#5F6368" />
                  ) : (
                    <MdExpandMore color="#5F6368" />
                  )
                }
              >
                <Typography
                  sx={{
                    fontWeight: "500",
                    fontSize: "16px",
                    lineHeight: "26px",
                    color: "#5F6368",
                  }}
                >
                  {t("chat.references")}
                </Typography>
              </Button>
            ) : (
              <Box />
            )}
            {/* Copy and feedback only make sense once the answer is complete */}
            {!isStreaming && (
              <Stack direction="row" gap="4px">
                <MessageUsage messageId={messageId ?? undefined} />
                <Tooltip
                  title={isCopied ? t("common.copied") : t("common.copy")}
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (isCopied) return;
                      copy(answer);
                    }}
                  >
                    {isCopied ? (
                      <IoCheckmarkDoneOutline color="blue" />
                    ) : (
                      <LuCopy />
                    )}
                  </IconButton>
                </Tooltip>
                {messageId && <TalkFeedback messageId={messageId} />}
              </Stack>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};
